<template>
  <div v-if="featureEnabled" class="jfc" data-testid="job-family-card">
    <!-- Breadcrumb navigation -->
    <ExBreadcrumbs
      class="jfc__breadcrumbs"
      :breadcrumbsList="breadcrumbs"
      @onClickBreadcrumb="handleBreadcrumbClick"
      data-testid="job-family-breadcrumbs"
    />

    <!-- Error state -->
    <div v-if="error" class="jfc__error" data-testid="job-family-error">
      <div class="jfc__error-content">
        <p class="jfc__error-message">Unable to load job family details</p>
        <button
          type="button"
          class="jfc__error-retry"
          @click="$emit('retry')"
          data-testid="retry-button"
        >
          Retry
        </button>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div v-else-if="loading" class="jfc__loading" data-testid="job-family-loading">
      <div class="jfc__skeleton-header">
        <div class="jfc__skeleton-line jfc__skeleton-line--title"></div>
        <div class="jfc__skeleton-line jfc__skeleton-line--subtitle"></div>
      </div>
      <div class="jfc__skeleton-tabs"></div>
      <div class="jfc__skeleton-content"></div>
    </div>

    <!-- Main content -->
    <div v-else class="jfc__content">
      <!-- Header: shared OrgObjectHeader (icon + status, name + code, chatter, overflow menu) -->
      <div class="jfc__header" data-testid="job-family-header">
        <OrgObjectHeader
          type="JobFamily"
          :status="family.status"
          :code="headerCode"
          :name="family.name"
          :archived="family.status === 'archived'"
          :currentPosition="family"
          :positionCount="family.positionCount || 0"
          data-testid="job-family-identity"
          @chatterClick="handleChatterClick"
          @optionClick="onHeaderOptionClick"
        />
      </div>

      <!-- Tab strip -->
      <div class="jfc__tabs" data-testid="job-family-tabs">
        <v-tabs v-model="internalActiveTab" class="jfc__tabs-nav">
          <v-tab data-testid="levels-tab">
            <span class="jfc__tab-label">Levels</span>
            <v-badge
              v-if="family.levelCount != null"
              :content="String(family.levelCount)"
              :value="true"
              inline
              :color="badgeColor('levels')"
              class="jfc__tab-badge"
            />
          </v-tab>
          <v-tab v-if="showContextTab" data-testid="context-tab">
            <span class="jfc__tab-label">Context</span>
            <v-badge
              v-if="showContextCountBadge"
              :content="String(contextBadgeCount)"
              :value="true"
              inline
              :color="badgeColor('context')"
              class="jfc__tab-badge"
            />
          </v-tab>
        </v-tabs>
      </div>

      <!-- Tab content -->
      <div class="jfc__tab-content">
        <v-tabs-items v-model="internalActiveTab">
          <!-- Levels tab -->
          <v-tab-item>
            <!-- Levels-only loading state: the family detail has loaded, but the
                 levels list is still in flight. -->
            <div
              v-if="levelsLoading"
              class="jfc__levels-loading"
              data-testid="levels-loading"
            >
              <div class="jfc__skeleton-line jfc__skeleton-line--levels"></div>
              <div class="jfc__skeleton-line jfc__skeleton-line--levels"></div>
              <div class="jfc__skeleton-line jfc__skeleton-line--levels"></div>
            </div>

            <!-- Levels-only error state: retry refetches only the levels list. -->
            <div
              v-else-if="levelsError"
              class="jfc__levels-error"
              data-testid="levels-error"
            >
              <p class="jfc__levels-error-message">Unable to load levels</p>
              <button
                type="button"
                class="jfc__error-retry"
                @click="$emit('retry-levels')"
                data-testid="retry-levels-button"
              >
                Retry
              </button>
            </div>

            <JobFamilyLevelsTable
              v-else
              :family-id="family.id"
              :levels="levels"
              :read-only="family.status === 'retired' || family.status === 'archived'"
              :busy="levelsBusy"
              :suggestion-active="suggestionActive"
              :undo-suggestion-busy="undoSuggestionBusy"
              :level-flags="levelFlags"
              @addLevel="$emit('add-level')"
              @editLevel="$emit('edit-level', $event)"
              @deleteLevel="$emit('delete-level', $event)"
              @reorder="$emit('reorder-levels', $event)"
              @suggest="$emit('suggest')"
              @undoSuggestions="$emit('undo-suggestions')"
              @dismissSuggestion="$emit('dismiss-suggestion')"
              data-testid="levels-table"
            />
          </v-tab-item>

          <!-- Context tab -->
          <v-tab-item v-if="showContextTab">
            <ContextTab
              :state="contextState"
              :context="contextData?.context || null"
              :context-updated-at="contextData?.context_updated_at || null"
              :context-updated-by="contextData?.context_updated_by || null"
              :files="contextFiles"
              :uploading-files="uploadingContextFiles"
              :disabled="isSavingContext"
              @save="handleContextSave"
              @upload="handleContextUpload"
              @remove="handleContextRemove"
              data-testid="context-tab-content"
            />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import OrgObjectHeader from "@components/OrgObjectHeader/OrgObjectHeader.vue";
import JobFamilyLevelsTable from "@components/JobFamilyLevelsTable/JobFamilyLevelsTable.vue";
import { displayCode } from "@components/JobFamiliesList/jobFamiliesListRow.js";
import ContextTab from "@components/ContextTab/ContextTab.vue";
import {
  LD_FLAG_IDEA_MDP_581,
  LD_FLAG_IDEA_MDP_494,
  LD_FLAG_IDEA_MDP_523,
} from "@components/constants/launchDarklyFlagKeys";
import launchDarklyService from "@/services/launchdarkly";

const props = defineProps({
  family: {
    type: Object,
    required: true,
    validator: (v) =>
      Boolean(
        v &&
          typeof v.id !== "undefined" &&
          typeof v.name === "string" &&
          typeof v.status === "string"
      ),
  },
  activeTab: {
    type: String,
    default: "levels",
    validator: (v) => ["levels", "context"].includes(v),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Object,
    default: null,
  },
  levels: {
    type: Array,
    default: () => [],
  },
  levelsLoading: {
    type: Boolean,
    default: false,
  },
  levelsError: {
    type: [Object, Error],
    default: null,
  },
  levelsBusy: {
    type: Boolean,
    default: false,
  },
  suggestionActive: {
    type: Boolean,
    default: false,
  },
  /** When true, the Undo Suggestions request is in flight (disables the action). */
  undoSuggestionBusy: {
    type: Boolean,
    default: false,
  },
  levelFlags: {
    type: Object,
    default: () => ({}),
  },
  contextData: {
    type: Object,
    default: null,
  },
  contextFiles: {
    type: Array,
    default: () => [],
  },
  uploadingContextFiles: {
    type: Array,
    default: () => [],
  },
  isSavingContext: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:activeTab",
  "retry",
  "retry-levels",
  "add-level",
  "edit-level",
  "delete-level",
  "reorder-levels",
  "suggest",
  "undo-suggestions",
  "dismiss-suggestion",
  "context-save",
  "context-upload",
  "context-remove",
  "chatter-click",
  "edit",
  "activate",
  "retire",
  "reactivate",
  "archive",
  "unarchive",
  "delete",
]);

// Feature flags — resolved on mount via the service's async getFlag API, the
// same way RoleCard.vue / PositionForm.vue gate their ContextTab.
const featureEnabled = ref(false);
const showContextTabV2 = ref(false);
const showContextCountBadge = ref(false);

onMounted(async () => {
  featureEnabled.value =
    (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_581, false)) === true;
  showContextTabV2.value =
    (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_494, false)) === true;
  showContextCountBadge.value =
    (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_523, false)) === true;
});

// Breadcrumbs configuration
const breadcrumbs = computed(() => [
  {
    text: "Job Families",
    href: "/organization/job-families",
    disabled: false,
  },
  {
    text: "Job Family",
    disabled: true,
  },
]);

// Internal tab state
const internalActiveTab = ref(props.activeTab === "context" ? 1 : 0);

watch(
  () => props.activeTab,
  (newTab) => {
    internalActiveTab.value = newTab === "context" ? 1 : 0;
  }
);

watch(internalActiveTab, (newIndex) => {
  const tabKey = newIndex === 1 ? "context" : "levels";
  emit("update:activeTab", tabKey);
});

// Header code, shown as "#<code>" like the other org-object headers
const headerCode = computed(() => displayCode(props.family?.code));

// Show context tab if flag enabled
const showContextTab = computed(() => showContextTabV2.value);

// Context state (empty/dirty/filled based on data)
const contextState = computed(() => {
  const hasContext = props.contextData?.context?.trim();
  const hasFiles = props.contextFiles.length > 0;
  if (!hasContext && !hasFiles) return "empty";
  return "filled";
});

// Context tab badge count
const contextBadgeCount = computed(() => {
  const fileCount = props.contextFiles.length || 0;
  const hasContext = props.contextData?.context?.trim() ? 1 : 0;
  return fileCount + hasContext;
});

// Badge color (active tab purple, inactive gray)
function badgeColor(tab) {
  const isActive =
    (tab === "levels" && internalActiveTab.value === 0) ||
    (tab === "context" && internalActiveTab.value === 1);
  return isActive ? "#f9f5ff" : "#f9fafb";
}

// Breadcrumb navigation
function handleBreadcrumbClick(item) {
  // ExBreadcrumbs self-navigates via href; no action needed
}

// Context handlers
function handleContextSave(value) {
  emit("context-save", value);
}

function handleContextUpload(files) {
  emit("context-upload", files);
}

function handleContextRemove(fileId) {
  emit("context-remove", fileId);
}

// Header actions
function handleChatterClick() {
  emit("chatter-click");
}

function onHeaderOptionClick(payload) {
  const type = payload && payload.item && payload.item.value;
  if (!type) return;
  if (type === "edit") emit("edit");
  else if (type === "activate") emit("activate");
  else if (type === "retire") emit("retire");
  else if (type === "reactivate") emit("reactivate");
  else if (type === "archive") emit("archive");
  else if (type === "unarchive") emit("unarchive");
  else if (type === "delete") emit("delete");
}
</script>

<style scoped>
.jfc {
  width: 100%;
}

.jfc__breadcrumbs {
  margin-bottom: 16px;
}

.jfc__error {
  padding: 48px 24px;
  text-align: center;
}

.jfc__error-content {
  max-width: 400px;
  margin: 0 auto;
}

.jfc__error-message {
  color: #344054;
  font-size: 14px;
  margin-bottom: 16px;
}

.jfc__error-retry {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  color: #ffffff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 18px;
  transition: background 0.2s;
}

.jfc__error-retry:hover {
  background: #6941c6;
  border-color: #6941c6;
}

.jfc__error-retry:focus {
  outline: 2px solid #f4ebff;
  outline-offset: 2px;
}

.jfc__loading {
  padding: 24px 0;
}

.jfc__skeleton-header {
  margin-bottom: 24px;
}

.jfc__skeleton-line {
  background: #f2f4f7;
  border-radius: 4px;
  height: 20px;
  margin-bottom: 12px;
}

.jfc__skeleton-line--title {
  width: 60%;
}

.jfc__skeleton-line--subtitle {
  width: 40%;
}

.jfc__skeleton-line--levels {
  height: 16px;
  margin-bottom: 16px;
}

.jfc__skeleton-line--levels:nth-child(1) {
  width: 30%;
}

.jfc__skeleton-line--levels:nth-child(2) {
  width: 55%;
}

.jfc__skeleton-line--levels:nth-child(3) {
  width: 45%;
}

.jfc__levels-loading {
  padding: 8px 0;
}

.jfc__levels-error {
  padding: 48px 24px;
  text-align: center;
}

.jfc__levels-error-message {
  color: #344054;
  font-size: 14px;
  margin-bottom: 16px;
}

.jfc__skeleton-tabs {
  background: #f2f4f7;
  border-radius: 4px;
  height: 48px;
  margin-bottom: 24px;
}

.jfc__skeleton-content {
  background: #f2f4f7;
  border-radius: 8px;
  height: 400px;
}

.jfc__content {
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}

.jfc__header {
  border-bottom: 1px solid #eaecf0;
  padding: 10px 0 0 0;
}

/* Same spacing offsets the Position / Role detail cards apply around the shared header. */
.jfc__header ::v-deep .header-header-container {
  left: -10px;
  position: relative;
}

.jfc__header ::v-deep .header-org-menu {
  right: 0;
}

.jfc__tabs {
  border-bottom: 1px solid #eaecf0;
  padding: 0 24px;
}

.jfc__tabs-nav ::v-deep .v-tabs-bar {
  background: transparent;
  border: none;
}

.jfc__tabs-nav ::v-deep .v-tab {
  color: #475467;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: normal;
  padding: 0 16px;
  text-transform: none;
}

.jfc__tabs-nav ::v-deep .v-tab--active {
  color: #7f56d9;
}

.jfc__tabs-nav ::v-deep .v-tabs-slider {
  background-color: #7f56d9;
  border-color: #d6bbfb;
}

.jfc__tab-label {
  margin-right: 8px;
}

.jfc__tab-badge ::v-deep .v-badge__badge {
  border-radius: 9999px;
  /* ER-11784 — Vuetify's v-badge `color` prop only sets the background; the
     text stays Vuetify's default white ($badge-color), which is unreadable on
     the light #f9f5ff / #f9fafb pills. Set the text colour and a real border,
     matching the shared HorizontalMenu count-badge (inactive state). */
  color: #344054;
  border: 1px solid;
  /* !important overrides the inline border-color the `:color` prop writes
     (same colour as the background, so otherwise the pill has no visible edge). */
  border-color: #eaecf0 !important;
  font-size: 12px;
  font-weight: 500;
  height: 20px;
  min-width: 20px;
  padding: 2px 8px;
}

.jfc__tabs-nav ::v-deep .v-tab--active .jfc__tab-badge .v-badge__badge {
  color: #6941c6;
  border-color: #e9d7fe !important;
}

.jfc__tab-content {
  padding: 24px;
}

/* ER-11825 — Vuetify's v-window (v-tabs-items) locks the tab pane height via an
   inline `height` on .v-window__container during the tab transition. That capped
   height is what makes the Levels table's own overflow container show a nested
   vertical scrollbar. Let the pane hug its content and scroll with the page. */
.jfc__tab-content ::v-deep .v-window__container {
  height: auto !important; /* override Vuetify's inline transition height */
}
</style>
